import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HoverCard, HoverCardContent, HoverCardPortal, HoverCardTrigger } from '@/app/components/ui/hover-card';
import type { ChatMessageToolCall } from '../../../types';

interface ToolContentProps {
  tool_calls: ChatMessageToolCall[];
  isActiveConversationTool?: boolean;
}

const ToolListContent = ({
  tool_calls,
  hideGradientMask = false,
}: {
  tool_calls: ChatMessageToolCall[];
  hideGradientMask?: boolean;
}) => {
  const shouldShowGradientMask = tool_calls.length > 3 && !hideGradientMask;
  const [isScrolledFromTop, setIsScrolledFromTop] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const listElement = listRef.current;
    if (!listElement) return;

    listElement.scrollTop = listElement.scrollHeight;
    setIsScrolledFromTop(listElement.scrollTop > 0);
  }, [tool_calls]);

  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    setIsScrolledFromTop(event.currentTarget.scrollTop > 0);
  }, []);

  return (
    <div className="flex max-h-[378px] items-stretch overflow-hidden">
      {hideGradientMask ? null : <div className="mx-4 w-0.5 flex-shrink-0 self-stretch bg-system-white08" />}
      <div className="relative min-h-0 flex-1 overflow-hidden">
        <div ref={listRef} className="h-full min-h-0 space-y-2 overflow-y-auto" onScroll={handleScroll}>
          {tool_calls.map((tool) => (
            <div key={tool.id} className="rounded-12 bg-system-white04 p-2">
              <div className="flex items-center gap-1 text-system-white text-xs leading-5">
                {tool.status === 'error' ? (
                  <span className="size-[5px] rounded-full bg-[#ff6666]" />
                ) : (
                  <span className="size-[5px] rounded-full bg-[#98ff66]" />
                )}
                <span>{tool.name}</span>
              </div>
              {tool.arguments && (
                <pre className="mt-0.5 overflow-x-auto whitespace-pre-wrap break-words bg-transparent p-0 text-system-white32 text-xs leading-5">
                  {tool.arguments}
                </pre>
              )}
              {tool.output && (
                <pre className="overflow-x-auto whitespace-pre-wrap break-words bg-transparent p-0 text-system-white32 text-xs leading-5">
                  {tool.output}
                </pre>
              )}
            </div>
          ))}
        </div>
        {shouldShowGradientMask && isScrolledFromTop && (
          <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-[linear-gradient(180deg,#1E2023_0%,_rgba(30,32,35,0)_100%)] light:bg-[linear-gradient(180deg,#F4F4FA_0%,_rgba(244,244,250,0)_100%)]" />
        )}
      </div>
    </div>
  );
};

export const ToolContent = ({ tool_calls, isActiveConversationTool = false }: ToolContentProps) => {
  const t = useTranslations();
  const ToolTitle = useMemo(() => {
    return (
      <div className="inline-flex w-fit cursor-default items-center gap-1 rounded-8 bg-system-white08 px-2 py-1 text-system-white64 text-xs">
        <span>{t('claw.chat.tool.usedTools', { count: tool_calls.length })}</span>
      </div>
    );
  }, [tool_calls.length]);

  if (isActiveConversationTool) {
    return (
      <>
        {ToolTitle}
        <ToolListContent tool_calls={tool_calls} />
      </>
    );
  }

  return (
    <HoverCard openDelay={100} closeDelay={100}>
      <HoverCardTrigger asChild={true}>{ToolTitle}</HoverCardTrigger>
      <HoverCardPortal>
        <HoverCardContent side="right" align="start" className="max-w-[660px] border-none bg-system-bg02 p-2 text-xs">
          <ToolListContent tool_calls={tool_calls} hideGradientMask={true} />
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  );
};
